import { Form, Formik } from 'formik';
import { useHistory } from 'react-router';
import { Loan } from '../../types/loan/Loan';
import { LoanReviewStatus, loanReviewStatusTitles } from '../../types/loan/LoanReviewStatus';
import { EditLoanPopupModel as EditLoanFormModel } from '../../types/loan/EditLoanFormModel';
import { LocationState } from '../../types/state/ui/LocationState';
import { useCloManagerUsers } from '../../effects/data-accessors/useCloManagerUsers';
import { useAppDispatch } from '../../effects/useAppDispatch';
import { useAppSelector } from '../../effects/useAppSelector';
import { loanActions } from '../../actions/loan.actions';
import { numericUtils, isRequesting, isRequestSuccess, moneyUtils, loanUtils, formatUtils } from '../../utils';
import { user } from '../../user';
import { SelectField } from '../common/form-fields/SelectField';
import { NumberField } from '../common/form-fields/NumberField';
import { TextareaField } from '../common/form-fields/TextareaField';
import { DropDownField } from './edit/DropDownField';
import { useLoanFieldValues } from '../../effects/data-accessors/useLoanFieldValues';
import { MultiSelectField } from '../forms/MultiSelectField';
import { MultiselectDisplayType } from '../controls';
import { RouteLeavingGuard } from '../routing';

interface EditLoanFormProps {
    loan: Loan;
    onCancel: () => void;
}

interface EditLoanFormValues {
    analystUserId: string;
    review: LoanReviewStatus;
    order: string;
    target: string;
    allocation: string;
    allocationPct: string;
    passingReasons: string[];
    otherPassingReasons: string;
}

const reviewOptions = [
    { value: LoanReviewStatus.InReview, label: loanReviewStatusTitles[LoanReviewStatus.InReview] },
    { value: LoanReviewStatus.Approved, label: loanReviewStatusTitles[LoanReviewStatus.Approved] },
    { value: LoanReviewStatus.Passed, label: loanReviewStatusTitles[LoanReviewStatus.Passed] },
];

export function EditLoanForm({ loan, onCancel }: EditLoanFormProps) {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { items: cloManagerUsers } = useCloManagerUsers(user.current()?.companyId);
    const { values: allFieldValues, requestState: fieldValuesRequestState } = useLoanFieldValues();
    const editPopupRequestState = useAppSelector(s => s.loan.editPopupRequestState);
    const isSaving = isRequesting(editPopupRequestState);

    const passingReasons = allFieldValues?.passingReasons || [];

    const analystOptions = Object
        .values(cloManagerUsers)
        .map(u => ({ value: String(u.id), label: `${u.firstName} ${u.lastName}` }));

    const initialValues: EditLoanFormValues = {
        analystUserId: loan.analystUserId == null ? '' : String(loan.analystUserId),
        review: loan.review || LoanReviewStatus.InReview,
        order: numericUtils.isNumber(loan.order) ? moneyUtils.amountToMM(loan.order) : '',
        target: numericUtils.isNumber(loan.target) ? moneyUtils.amountToMM(loan.target) : '',
        allocation: numericUtils.isNumber(loan.allocation) ? moneyUtils.amountToMM(loan.allocation) : '',
        allocationPct: numericUtils.isNumber(loan.allocationPct)
            ? formatUtils.formatDecimal(loan.allocationPct, 2) || ''
            : '',
        otherPassingReasons: loan.otherPassingReasons || '',
        passingReasons: [...(loan.passingReasons || [])].sort(),
    };

    const handleSubmit = (values: EditLoanFormValues) => {
        const millionMultiplier = 1.0e+6;

        const parseNumeric = (value: string) =>
            numericUtils.numberOrDefault(value.replace(/,/g, ''), null);

        const isPassed = values.review === LoanReviewStatus.Passed;

        const order = parseNumeric(values.order);
        const target = parseNumeric(values.target);
        const allocation = parseNumeric(values.allocation);

        const model: EditLoanFormModel = {
            analystUserId: values.analystUserId ? Number(values.analystUserId) : null,
            review: values.review,
            order: isPassed || order == null ? null : order * millionMultiplier,
            target: isPassed || target == null ? null : target * millionMultiplier,
            allocation: isPassed || allocation == null ? null : allocation * millionMultiplier,
            allocationPct: isPassed ? null : parseNumeric(values.allocationPct),
            otherPassingReasons: isPassed ? values.otherPassingReasons.trim() || null : null,
            passingReasons: isPassed && values.passingReasons.length > 0 ? values.passingReasons : null,
        };
        dispatch(loanActions.loanUpdated(loan.id, model));
    };

    const handleNavigate = (pathname: string, search: string, state: LocationState) => {
        const nextLocation = { pathname, search, state };

        if (pathname === history.location.pathname) {
            history.replace(nextLocation);
        } else {
            history.push(nextLocation);
        }
    };

    const handleCancel = () => {
        if (!isSaving) {
            onCancel();
        }
    };

    return (
        <Formik<EditLoanFormValues>
            initialValues={initialValues}
            key={loan.id}
            onSubmit={handleSubmit}
        >
            {({ dirty, values, setFieldValue }) => {
                const recalcAllocationPct = () => {
                    const orderRaw = String(values.order ?? '').replace(/,/g, '');
                    const allocationRaw = String(values.allocation ?? '').replace(/,/g, '');
                    setFieldValue(
                        'allocationPct',
                        numericUtils.isNumber(allocationRaw) && numericUtils.isNumber(orderRaw) && Number(orderRaw) !== 0
                            ? formatUtils.formatDecimal(loanUtils.calculateAllocationPct(Number(allocationRaw), Number(orderRaw)), 2)
                            : ''
                    );
                };

                return (
                    <Form className="edit-loan-form">
                        <RouteLeavingGuard
                            navigate={handleNavigate}
                            shouldBlockNavigation={() => dirty && !isSaving}
                        />
                        <div className="edit-loan-form-grid">
                            <DropDownField
                                label="Analyst"
                                name="analystUserId"
                                options={analystOptions.map(o => ({ value: o.value, text: o.label }))}
                                placeholder="Select"
                                disabled={isSaving}
                                withEmptyValue
                                withSearch
                            />
                            <SelectField
                                label="Review"
                                name="review"
                                values={reviewOptions}
                                optionKeyProp="value"
                                optionLabelProp="label"
                                disabled={isSaving}
                            />
                        </div>
                        {values.review === LoanReviewStatus.Passed &&
                            <>
                                <MultiSelectField
                                    name="passingReasons"
                                    label="Reason(s) for passing"
                                    dataItems={passingReasons.map(r => ({ value: r.name, text: r.name, groupKey: r.category }))}
                                    placeholder="Select"
                                    wrapperClassName="passing-reasons-multiselect"
                                    horizontalOrientation
                                    withSearch
                                    disabled={isSaving || !isRequestSuccess(fieldValuesRequestState)}
                                    displayType={MultiselectDisplayType.ChipList}
                                />
                                <TextareaField
                                    label="Other reason(s) for passing"
                                    name="otherPassingReasons"
                                    placeholder="Enter other reason(s) for passing"
                                    capitalize={false}
                                    maxLength={2048}
                                    disabled={isSaving}
                                    wrapperClassName="edit-loan-form-textarea"
                                />
                            </>
                        }
                        {values.review !== LoanReviewStatus.Passed &&
                            < div className="edit-loan-form-grid">
                                <NumberField
                                    label="Order, MM"
                                    name="order"
                                    placeholder="Enter Order"
                                    disabled={isSaving}
                                    decimalLimit={3}
                                    autoFormat
                                    onBlur={recalcAllocationPct}
                                />
                                <NumberField
                                    label="Target, MM"
                                    name="target"
                                    placeholder="Enter Target"
                                    disabled={isSaving}
                                    decimalLimit={3}
                                    autoFormat
                                />
                                <NumberField
                                    label="Allocation, MM"
                                    name="allocation"
                                    placeholder="Enter Allocation"
                                    disabled={isSaving}
                                    decimalLimit={3}
                                    autoFormat
                                    onBlur={recalcAllocationPct}
                                />
                                <NumberField
                                    label="Allocation, %"
                                    name="allocationPct"
                                    placeholder="Enter Allocation"
                                    disabled={isSaving}
                                    autoFormat
                                />
                            </div>
                        }
                        <div className="edit-loan-form-footer flex-row">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-main"
                                disabled={isSaving || !dirty}
                            >
                                Save
                            </button>
                        </div>
                    </Form>
                );
            }}
        </Formik >
    );
}
